import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { Icon } from 'xm-spark';

import { HELP_TABS } from 'xm-constants/helpView';
import { showHelp as showHelpAction } from 'actions/helpPopover';
import useAction from 'hooks/useAction';
import { isHelpLinksDisabled } from 'selectors/featureToggles';
import { useHelpLinks } from './HelpLinksProvider';

export function useHelpClickHandler({
  helpKey,
  href: hrefProp,
  onClick: onClickProp,
  actsAsToggle,
}) {
  const showHelp = useAction(showHelpAction);
  const { helpLinks, normalizeHelpLink } = useHelpLinks();
  const href = hrefProp || helpLinks[helpKey] || normalizeHelpLink(helpKey);

  const onClick = e => {
    onClickProp?.(e);
    e.preventDefault();
    showHelp(href, { actsAsToggle, tab: HELP_TABS.HELP });
  };

  return { href, onClick };
}

export default function HelpLink({
  children,
  actsAsToggle,
  helpKey,
  href: hrefProp,
  onClick: onClickProp,
  ...otherProps
}) {
  const { href, onClick } = useHelpClickHandler({
    helpKey,
    href: hrefProp,
    onClick: onClickProp,
    actsAsToggle,
  });

  return (
    <a
      href={href}
      onClick={onClick}
      rel="noopener noreferrer"
      target="_blank"
      {...otherProps}
    >
      {children}
    </a>
  );
}

HelpLink.displayName = 'HelpLink';
HelpLink.propTypes = {
  children: PropTypes.node.isRequired,

  // Use either helpKey or href to provide the target address.
  // href gets priority as it's more specific; fallsback to using helpKey to lookup an address:
  helpKey: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
};
HelpLink.defaultProps = {
  helpKey: null,
  href: null,
  onClick: null,
};

export function DialogHeaderHelpLink({ helpKey, href, testId }) {
  const isLinkDisabled = useSelector(isHelpLinksDisabled);
  if (isLinkDisabled) return null;

  return (
    <HelpLink helpKey={helpKey} href={href} data-testid={testId}>
      <Icon type="icon-question" />
    </HelpLink>
  );
}
DialogHeaderHelpLink.displayName = 'DialogHeaderHelpLink';
DialogHeaderHelpLink.propTypes = {
  helpKey: PropTypes.string,
  href: PropTypes.string,
  testId: PropTypes.string,
};
DialogHeaderHelpLink.defaultProps = {
  helpKey: null,
  href: null,
  testId: 'helpButton',
};
